/**
 * Block type selector: icon buttons switch block type; full media UI lives in BlockEditor
 * (ImageEdit / AudioEdit / VideoEdit), not in a separate upload dialog.
 */

import * as React from 'react';
import {
    Button,
    cn,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '@ai-content-hub/content-kit';
import { Type, Image, Volume2, Video, FileText, Plus, FileStack } from 'lucide-react';
import type { BlockContentType } from './types';
import { useElementUITranslations } from '../i18n/elementUI';

/**
 * Content type option configuration
 */
export interface BlockTypeOption {
    type: BlockContentType;
    label: string;
    icon: typeof Type;
    /** Media kinds (image/audio/video); still switched via onChange like other types */
    isMedia?: boolean;
    /** Whether this option is disabled */
    isDisabled?: boolean;
    /** Whether this option is hidden */
    isHidden?: boolean;
}

/**
 * Default content type options
 */
export const DEFAULT_BLOCK_TYPES: BlockTypeOption[] = [
    { type: 'rich-text', label: 'Rich Text', icon: FileText },
    { type: 'office-document', label: 'Office', icon: FileStack },
    { type: 'image', label: 'Image', icon: Image, isMedia: true },
    { type: 'audio', label: 'Audio', icon: Volume2, isMedia: true },
    { type: 'video', label: 'Video', icon: Video, isMedia: true },
    { type: 'text', label: 'Text', icon: Type, isHidden: true },
];

/** Layout mode: separate media buttons vs one merged media button */
export type BlockTypeSelectorLayout = 'default' | 'media-merged';

export interface BlockTypeSelectorProps {
    /** Currently selected content type */
    value: BlockContentType;
    /** Callback when type changes (all types, including image/audio/video) */
    onChange: (type: BlockContentType) => void;
    /** Whether selector is disabled */
    disabled?: boolean;
    /** Available content types (defaults to all) */
    allowedTypes?: BlockContentType[];
    /** Button size */
    size?: 'sm' | 'default' | 'lg';
    /** Show text on buttons (default true) */
    showText?: boolean;
    /** Layout: default = separate buttons, media-merged = one Media dropdown */
    layout?: BlockTypeSelectorLayout;
    /** Additional class names */
    className?: string;
}

export const BlockTypeSelector: React.FC<BlockTypeSelectorProps> = ({
    value,
    onChange,
    disabled = false,
    allowedTypes,
    size = 'sm',
    showText = true,
    layout = 'default',
    className,
}) => {
    const et = useElementUITranslations();
    const options = React.useMemo(() => {
        let types = DEFAULT_BLOCK_TYPES;
        if (allowedTypes && allowedTypes.length > 0) {
            types = types.filter((opt) => allowedTypes.includes(opt.type));
        }
        return types.filter((opt) => !opt.isHidden);
    }, [allowedTypes]);

    const nonMediaOptions = options.filter((o) => !o.isMedia);
    const mediaOptions = options.filter((o) => o.isMedia);

    const iconSizeClass = cn(
        size === 'sm' && 'h-4 w-4',
        size === 'default' && 'h-4 w-4',
        size === 'lg' && 'h-5 w-5'
    );

    const buttonSizeClass = cn(
        size === 'sm' && 'h-8 p-1',
        size === 'default' && 'h-9 p-2',
        size === 'lg' && 'h-10 p-3'
    );

    const renderTypeButton = (type: BlockContentType, label: string, Icon: typeof Type, isDisabled?: boolean) => {
        const isSelected = value === type;
        const isButtonDisabled = disabled || isDisabled;
        return (
            <Button
                key={type}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                size={size}
                onClick={() => onChange(type)}
                disabled={isButtonDisabled}
                className={cn(
                    'block-type-selector-button shrink-0 cursor-pointer',
                    isSelected && 'block-type-selector-button--selected',
                    isButtonDisabled && 'block-type-selector-button--disabled',
                    buttonSizeClass,
                    isDisabled && 'opacity-50 cursor-not-allowed',
                    'p-0'
                )}
                title={isDisabled ? `${label} (coming soon)` : label}
            >
                <Icon className={iconSizeClass} />
                {showText && <span className="ml-1.5">{label}</span>}
            </Button>
        );
    };

    const isMediaSelected = mediaOptions.some((o) => value === o.type);

    return (
        <div
            className={cn(
                'block-type-selector flex gap-1 flex-nowrap items-center overflow-x-auto',
                className
            )}
        >
            {layout === 'default' &&
                options.map(({ type, label, icon: Icon, isDisabled }) =>
                    renderTypeButton(type, label, Icon, isDisabled)
                )}

            {layout === 'media-merged' && (
                <>
                    {nonMediaOptions.map(({ type, label, icon: Icon, isDisabled }) =>
                        renderTypeButton(type, label, Icon, isDisabled)
                    )}
                    {mediaOptions.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    variant={isMediaSelected ? 'default' : 'outline'}
                                    disabled={disabled}
                                    className={cn(
                                        'block-type-selector-media-button shrink-0 border-none p-0 cursor-pointer',
                                        !isMediaSelected && 'text-muted-foreground',
                                        buttonSizeClass,
                                        'px-0'
                                    )}
                                    title={et.mediaTitle}
                                    size="icon-sm"
                                >
                                    <Plus className={cn(iconSizeClass, '!w-5 !h-5 mx-0.5')} />
                                    {showText && <span className="ml-1.5">{et.media}</span>}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="block-type-selector-media-dropdown min-w-[9rem]"
                            >
                                {mediaOptions.map(({ type, label, icon: Icon, isDisabled }) => (
                                    <DropdownMenuItem
                                        key={type}
                                        disabled={disabled || isDisabled}
                                        className="block-type-selector-media-item cursor-pointer gap-2"
                                        onSelect={() => onChange(type)}
                                    >
                                        <Icon className="h-4 w-4 shrink-0" />
                                        <span>{label}</span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </>
            )}
        </div>
    );
};

export default BlockTypeSelector;
